/**
 * Print preview modal for 刻意练习 only.
 * Independent of RevisionPrintPreviewModal — do not merge layouts.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { LandPlot, Printer, Download, X, Info } from 'lucide-react';

import { requestClosePrintPreview } from './presentSystemPrint';
import {
  REVISION_PRINT_A4_HEIGHT_PX,
  REVISION_PRINT_A4_WIDTH_PX,
  resolveRevisionPrintPreviewScale,
  type RevisionPrintParticle,
} from './RevisionPrintAction';
import {
  PAS_DP_PRINT_PARTICLES_PER_PAGE,
  buildPasDeliberatePracticePrintHtml,
  chunkPasDeliberatePracticePrintParticles,
  filterPasDeliberatePracticePrintParticles,
} from './PasDeliberatePracticePrintAction';

function PasDpContinuousPreview({
  particles,
  sessionId,
  printSessionId,
  viewportWidth,
}: {
  particles: RevisionPrintParticle[];
  sessionId?: string | null;
  printSessionId?: string | null;
  viewportWidth: number;
}) {
  const [docLoading, setDocLoading] = useState(true);
  const pages = useMemo(
    () => chunkPasDeliberatePracticePrintParticles(particles),
    [particles],
  );
  const pageCount = Math.max(1, pages.length);
  const scale = resolveRevisionPrintPreviewScale(viewportWidth);
  const shellWidth = Math.round(REVISION_PRINT_A4_WIDTH_PX * scale);
  const pageGapPx = 16;
  const bodyPadY = 16 + 24;
  const documentHeight = bodyPadY
    + pageCount * REVISION_PRINT_A4_HEIGHT_PX
    + Math.max(0, pageCount - 1) * pageGapPx;
  const shellHeight = Math.round(documentHeight * scale);

  const documentHtml = useMemo(
    () => buildPasDeliberatePracticePrintHtml({
      particles,
      sessionId,
      printSessionId: printSessionId || '',
      previewViewportWidth: REVISION_PRINT_A4_WIDTH_PX + 32,
    }),
    [particles, printSessionId, sessionId],
  );

  useEffect(() => {
    setDocLoading(true);
  }, [documentHtml]);

  return (
    <div
      className="relative mx-auto shrink-0 overflow-hidden rounded-lg bg-white shadow-[0_8px_28px_rgba(15,23,42,0.12)]"
      style={{
        width: shellWidth,
        height: shellHeight,
        minWidth: shellWidth,
        minHeight: shellHeight,
        flexShrink: 0,
      }}
    >
      <iframe
        title="dp-print-preview-continuous"
        srcDoc={documentHtml}
        scrolling="no"
        className="border-0 bg-white"
        style={{
          width: REVISION_PRINT_A4_WIDTH_PX,
          height: documentHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          overflow: 'hidden',
          display: 'block',
        }}
        onLoad={() => setDocLoading(false)}
        onError={() => setDocLoading(false)}
      />
      {docLoading ? (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-white/55">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        </div>
      ) : null}
    </div>
  );
}

export type PasDeliberatePracticePrintPreviewModalProps = {
  visible: boolean;
  particles: RevisionPrintParticle[];
  resolvingEmbeddedImages?: boolean;
  sessionId?: string | null;
  printSessionId?: string | null;
  printing?: boolean;
  sharing?: boolean;
  onClose: () => void;
  onPrint: () => void;
  onShare: () => void;
};

export function PasDeliberatePracticePrintPreviewModal({
  visible,
  particles,
  resolvingEmbeddedImages = false,
  sessionId,
  printSessionId,
  printing = false,
  sharing = false,
  onClose,
  onPrint,
  onShare,
}: PasDeliberatePracticePrintPreviewModalProps) {
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window !== 'undefined' ? Math.min(window.innerWidth, 960) : 800
  ));

  useEffect(() => {
    if (!visible) return undefined;
    const sync = () => setViewportWidth(Math.min(window.innerWidth, 960));
    sync();
    window.addEventListener('resize', sync);
    return () => window.removeEventListener('resize', sync);
  }, [visible]);

  const printableParticles = useMemo(
    () => filterPasDeliberatePracticePrintParticles(particles),
    [particles],
  );
  const pages = useMemo(
    () => chunkPasDeliberatePracticePrintParticles(printableParticles),
    [printableParticles],
  );
  const busy = printing || sharing;
  const hasPrintableParticles = printableParticles.length > 0;
  const actionsLocked = busy || !hasPrintableParticles;

  const handleRequestClose = useCallback(() => {
    requestClosePrintPreview({
      busy,
      onClose,
      title: '正在执行打印任务',
      message: '是否退出？当前任务可能较慢或卡住，退出后可重新打开打印预览。',
      stayLabel: '继续等待',
      exitLabel: '退出',
    });
  }, [busy, onClose]);

  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleRequestClose();
      }
    };
    window.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [handleRequestClose, visible]);

  if (!visible) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-[#f8fafc]">
      <header className="flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-5 py-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <LandPlot className="h-[18px] w-[18px] text-blue-700" strokeWidth={2.2} />
            <h2 className="truncate text-base font-extrabold text-slate-900">打印刻意练习</h2>
          </div>
          <p className="mt-0.5 text-xs font-semibold text-slate-500">
            {`${printableParticles.length} 道题 · ${pages.length} 页 · 每页 ${PAS_DP_PRINT_PARTICLES_PER_PAGE} 题 · 无二维码`}
            {resolvingEmbeddedImages ? ' · 嵌入图后台准备中' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={handleRequestClose}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100 text-slate-800 hover:bg-slate-200"
          aria-label="关闭"
        >
          <X className="h-5 w-5" />
        </button>
      </header>

      <div className="flex items-center gap-2 border-b border-slate-200 bg-white px-5 py-3">
        <button
          type="button"
          onClick={onPrint}
          disabled={actionsLocked}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-700 px-3 text-sm font-extrabold text-white disabled:opacity-50"
        >
          <Printer className="h-[18px] w-[18px]" />
          {printing ? '正在调用打印' : '系统打印'}
        </button>
        <button
          type="button"
          onClick={onShare}
          disabled={actionsLocked}
          className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-slate-50 px-3 text-sm font-extrabold text-blue-700 disabled:opacity-50"
        >
          <Download className="h-[18px] w-[18px]" />
          {sharing ? '正在生成' : '生成 PDF'}
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-5 py-2 text-xs font-semibold text-slate-500">
        <Info className="h-4 w-4 shrink-0" />
        <span>本预览专用于刻意练习：无二维码，每页 5 题；不影响其他复习流的打印布局。</span>
      </div>

      <div className="relative min-h-0 flex-1 overflow-y-auto bg-[#eef2f6]">
        {!printSessionId || !hasPrintableParticles ? (
          <div className="flex h-full items-center justify-center px-8 text-center text-sm font-semibold text-slate-500">
            {particles.length
              ? '当前没有选中的知识颗粒，请在上方标签中重新开启参与打印。'
              : '暂无可预览内容'}
          </div>
        ) : (
          <div className="flex justify-center px-4 py-4">
            <PasDpContinuousPreview
              particles={printableParticles}
              sessionId={sessionId}
              printSessionId={printSessionId}
              viewportWidth={viewportWidth}
            />
          </div>
        )}
      </div>
    </div>
  );
}
